import type { TableProps, TableHeaderProps, TableBodyProps, TableFooterProps, TableRowProps, TableHeadProps, TableCellProps, TableCaptionProps } from './table.types'
/**
 * Identical API.
 */
import type * as React from 'react'
import { cn } from '../lib/utils'

export function Table({
  className,
  ...props
}: TableProps): React.ReactElement {
  return (
    <div
      className="relative w-full overflow-x-auto overflow-y-hidden rounded-[28px]"
      data-slot="table-container"
    >
      <table
        className={cn('w-full caption-bottom text-sm', className)}
        data-slot="table"
        {...props}
      />
    </div>
  )
}

export function TableHeader({
  className,
  ...props
}: TableHeaderProps): React.ReactElement {
  return (
    <thead
      className={cn('[&_tr]:border-b', className)}
      data-slot="table-header"
      {...props}
    />
  )
}

export function TableBody({
  className,
  ...props
}: TableBodyProps): React.ReactElement {
  return (
    <tbody
      className={cn('[&_tr:last-child]:border-0', className)}
      data-slot="table-body"
      {...props}
    />
  )
}

export function TableFooter({
  className,
  ...props
}: TableFooterProps): React.ReactElement {
  return (
    <tfoot
      className={cn('border-t font-medium [&>tr]:last:border-b-0', className)}
      data-slot="table-footer"
      {...props}
    />
  )
}

export function TableRow({
  className,
  ...props
}: TableRowProps): React.ReactElement {
  return (
    <tr
      className={cn('border-b transition-colors', className)}
      data-slot="table-row"
      {...props}
    />
  )
}

export function TableHead({
  className,
  ...props
}: TableHeadProps): React.ReactElement {
  return (
    <th
      className={cn(
        'text-on-surface-secondary h-10 p-4 text-left align-middle leading-none font-medium whitespace-nowrap has-[[role=checkbox]]:pe-0',
        className,
      )}
      data-slot="table-head"
      {...props}
    />
  )
}

export function TableCell({
  className,
  ...props
}: TableCellProps): React.ReactElement {
  return (
    <td
      className={cn(
        'p-4 align-middle leading-none whitespace-nowrap has-[[role=checkbox]]:pe-0',
        className,
      )}
      data-slot="table-cell"
      {...props}
    />
  )
}

export function TableCaption({
  className,
  ...props
}: TableCaptionProps): React.ReactElement {
  return (
    <caption
      className={cn('text-on-surface-secondary mt-4 text-sm', className)}
      data-slot="table-caption"
      {...props}
    />
  )
}
